import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MenuService } from 'src/app/components/menu/service/menu.service';
import { HomeActionItem, HomeSearchGroup } from 'src/app/pages/home/models/home.models';
import { LoggedInService } from 'src/app/services/auth/loggedin.service';
import { PlatformHeaderService } from './platform-header.service';
import {
  AuthorizationUserProfile,
  getAuthorizationDisplayName,
  getFirstTwoNameInitials,
} from './platform-header-user.model';

@Component({
  standalone: false,
  selector: 'app-platform-header',
  templateUrl: './platform-header.component.html',
  styleUrls: ['./platform-header.component.scss'],
})
export class PlatformHeaderComponent implements OnInit, OnDestroy {
  private readonly subscription = new Subscription();

  title = '';
  subtitle = '';
  showSubtitle = true;
  userInitials = '';
  userDisplayName = '';

  searchGroups: HomeSearchGroup[] = [];
  filteredSearchGroups: HomeSearchGroup[] = [];
  searchDropdownOpen = false;
  userMenuOpen = false;
  searchTerm = '';

  constructor(
    private readonly platformHeaderService: PlatformHeaderService,
    private readonly menuService: MenuService,
    private readonly loggedInService: LoggedInService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.platformHeaderService.title$.subscribe(title => {
        this.title = title;
      }),
    );

    this.subscription.add(
      this.platformHeaderService.subtitle$.subscribe(subtitle => {
        this.subtitle = subtitle;
      }),
    );

    this.subscription.add(
      this.platformHeaderService.showSubtitle$.subscribe(showSubtitle => {
        this.showSubtitle = showSubtitle;
      }),
    );

    this.loadUserProfile();
    this.loadSearchGroups();
    this.platformHeaderService.loadSubtitle();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target;

    if (!(target instanceof Element)) {
      this.searchDropdownOpen = false;
      this.userMenuOpen = false;
      return;
    }

    if (!target.closest('.platform-header__search')) {
      this.searchDropdownOpen = false;
    }

    if (!target.closest('.platform-header__user-area')) {
      this.userMenuOpen = false;
    }
  }

  get hasSearchOptions(): boolean {
    return this.searchGroups.some(group => group.items.length > 0);
  }

  onSearchFocus(): void {
    this.searchDropdownOpen = true;
    this.userMenuOpen = false;
    this.applySearchFilter();
  }

  onSearchInput(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value;
    this.applySearchFilter();
    this.searchDropdownOpen = true;
  }

  onUserMenuClick(event: Event): void {
    event.stopPropagation();
    this.userMenuOpen = !this.userMenuOpen;
    this.searchDropdownOpen = false;
  }

  onSearchItemClick(item: HomeActionItem, event: Event): void {
    event.stopPropagation();
    this.router.navigateByUrl(`/${item.link}`);
    this.searchDropdownOpen = false;
  }

  onLogout(event: Event): void {
    event.stopPropagation();
    this.loggedInService.redirectlogout();
  }

  trackSearchGroup(_index: number, group: HomeSearchGroup): string {
    return group.id;
  }

  trackSearchItem(_index: number, item: HomeActionItem): string {
    return item.id;
  }

  private loadUserProfile(): void {
    const applyProfile = (profile: AuthorizationUserProfile | null): void => {
      const displayName = getAuthorizationDisplayName(profile);

      if (displayName) {
        this.userDisplayName = displayName;
        this.userInitials = getFirstTwoNameInitials(displayName);
        return;
      }

      const tokenName = this.loggedInService.tokenContent$.getValue()?.nomeUsuario ?? '';
      this.userDisplayName = tokenName.trim();
      this.userInitials = getFirstTwoNameInitials(this.userDisplayName);
    };

    applyProfile(this.menuService.userProfile$.getValue());

    this.subscription.add(this.menuService.userProfile$.subscribe(applyProfile));

    if (!this.menuService.isMenuLoaded()) {
      this.subscription.add(
        this.menuService.whenMenuLoaded$().subscribe(() => {
          applyProfile(this.menuService.userProfile$.getValue());
        }),
      );
    }
  }

  private loadSearchGroups(): void {
    const applyGroups = (): void => {
      this.searchGroups = this.menuService.getHomeSearchGroups();
      this.applySearchFilter();
    };

    if (this.menuService.isMenuLoaded()) {
      applyGroups();
    }

    this.subscription.add(
      this.menuService.whenMenuLoaded$().subscribe(() => applyGroups()),
    );

    this.subscription.add(
      this.menuService.sidebarNav$.subscribe(nav => {
        if (nav) {
          applyGroups();
        }
      }),
    );
  }

  private applySearchFilter(): void {
    const normalizedTerm = this.searchTerm.trim().toLowerCase();

    if (!normalizedTerm) {
      this.filteredSearchGroups = this.searchGroups;
      return;
    }

    this.filteredSearchGroups = this.searchGroups
      .map(group => {
        const groupMatches = group.title.toLowerCase().includes(normalizedTerm);
        const items = groupMatches
          ? group.items
          : group.items.filter(
              item =>
                item.label.toLowerCase().includes(normalizedTerm) ||
                item.link.toLowerCase().includes(normalizedTerm),
            );

        return { ...group, items };
      })
      .filter(group => group.items.length > 0);
  }
}
